import { useState, useRef } from 'react';
import { EMAIL_REQUIRED, EMAIL_INVALID, PASSWORD_REQUIRED } from '../constants/errorMessages';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Custom hook to manage the state and validation of a login form.
 *
 * @returns {object} - Returns an object containing:
 * - `email`: The current value of the email input.
 * - `password`: The current value of the password input.
 * - `emailError`: The error message for the email field (if any).
 * - `passwordError`: The error message for the password field (if any).
 * - `emailErrorRef`: A ref for the email error (useful for accessibility or focus management).
 * - `passwordErrorRef`: A ref for the password error.
 * - `handleEmailChange`: A function to handle changes in the email input.
 * - `handleEmailBlur`: A function triggered when the email input loses focus, used for validation.
 * - `handlePasswordChange`: A function to handle changes in the password input.
 * - `validateForm`: A function to validate the form and return a boolean indicating its validity.
 * - `isLoginReady`: A boolean flag that is true when the form is ready to be submitted.
 */
export const useLoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const emailErrorRef = useRef(null);
  const passwordErrorRef = useRef(null);

  const validateEmail = (email) => {
    if (email === '') {
      return EMAIL_REQUIRED;
    } else if (!emailRegex.test(email)) {
      return EMAIL_INVALID;
    } else {
      return '';
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailError(validateEmail(email));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError('');

    if (value === '') {
      setPasswordError(PASSWORD_REQUIRED);
    }
  };

  const validateForm = () => {
    const emailValidationError = validateEmail(email);
    setEmailError(emailValidationError);

    let formIsValid = true;

    if (emailValidationError) {
      formIsValid = false;
    }

    if (!password) {
      setPasswordError(PASSWORD_REQUIRED);
      formIsValid = false;
    }

    return formIsValid;
  };

  const isLoginReady = email && password && !validateEmail(email);

  return {
    email,
    password,
    emailError,
    passwordError,
    emailErrorRef,
    passwordErrorRef,
    handleEmailChange,
    handleEmailBlur,
    handlePasswordChange,
    validateForm,
    isLoginReady,
  };
};
